/**
 * La puerta de Soham Central.
 *
 * Regla de publicación (plan del sistema, 2026-08-17): la plataforma NO se sirve en
 * una URL pública sin pantalla de sesión. Esta compuerta envuelve a toda la app en
 * `main.jsx` y solo se abre con un usuario autenticado QUE ADEMÁS tenga rol asignado
 * en sus custom claims — una cuenta creada por fuera (el endpoint público de registro
 * de Firebase Auth existe siempre) entra a un vestíbulo vacío, no a la plataforma.
 *
 * Exenciones de desarrollo, nunca de producción:
 *   VITE_SIN_SESION=1  → vive en .env.development.local, que Vite NO lee al compilar.
 *   VITE_EMULADOR=1    → trabajo sin nube contra el emulador local.
 *
 * Sin conexión: Firebase Auth persiste la sesión en el navegador. La primera entrada
 * necesita internet; después la compuerta se abre con la sesión cacheada — condición
 * necesaria para levantar en una casa sin señal.
 */
import { useEffect, useState } from 'react'
import { observarSesion, entrar, salir, rolActual } from '../firebase.js'
import { MARCA } from '../marca.js'
import { FOTO_PORTADA } from '../fotos.js'
import Aliado from './Aliado.jsx'

const EXENTA =
  import.meta.env.VITE_SIN_SESION === '1' || import.meta.env.VITE_EMULADOR === '1'

const MENSAJES = {
  'auth/invalid-credential': 'Correo o contraseña incorrectos.',
  'auth/wrong-password': 'Correo o contraseña incorrectos.',
  'auth/user-not-found': 'Correo o contraseña incorrectos.',
  'auth/invalid-email': 'Ese correo no tiene un formato válido.',
  'auth/user-disabled': 'Esta cuenta está deshabilitada. Habla con el administrador.',
  'auth/too-many-requests': 'Demasiados intentos. Espera unos minutos y vuelve a intentar.',
  'auth/network-request-failed': 'Sin conexión. La primera entrada necesita internet.',
  'auth/operation-not-allowed': 'El inicio de sesión aún no está habilitado en el proyecto.',
  'auth/configuration-not-found': 'El inicio de sesión aún no está habilitado en el proyecto.'
}

export default function PuertaSesion({ children }) {
  // 'cargando' | 'login' | 'sin-rol' | 'abierta'
  const [estado, setEstado] = useState(EXENTA ? 'abierta' : 'cargando')
  const [rol, setRol] = useState(null)

  useEffect(() => {
    if (EXENTA) return undefined
    return observarSesion(async u => {
      if (!u) { setRol(null); setEstado('login'); return }
      const r = await rolActual()
      setRol(r)
      setEstado(r ? 'abierta' : 'sin-rol')
    })
  }, [])

  /* El aliado no ve el cotizador: su Central es la superficie de encargos. */
  if (estado === 'abierta') return rol === 'aliado' ? <Aliado /> : children
  if (estado === 'cargando') {
    return <div className="app sesion"><div className="empty">Comprobando sesión…</div></div>
  }
  if (estado === 'sin-rol') return <SinRol />
  return <Entrada />
}

/** La mitad fotográfica del panel: el color entra por la fotografía, como en todo
    el sistema. En teléfono se oculta y queda la caja sola. */
function LadoFoto() {
  return (
    <div className="sesion-foto" aria-hidden="true">
      <img src={FOTO_PORTADA} alt="" />
      <span className="sesion-frase">{MARCA.fraseMadre}</span>
    </div>
  )
}

function Identidad() {
  return (
    <div className="sesion-id">
      <img className="sesion-logo" src="/isotipo-soham.png" alt="" />
      <div>
        <b className="sesion-marca">{MARCA.plataforma}</b>
        <span className="sesion-de">de {MARCA.respaldo}</span>
      </div>
    </div>
  )
}

/** Botón de cierre de sesión para cualquier barra. Se dibuja solo cuando hay
    sesión real — en modo exento de desarrollo no hay nada que cerrar. Existe
    porque Juan puede entrar desde una computadora ajena y no debe quedar nada
    abierto atrás de él. */
/**
 * El pie de cuenta. Cerrar sesión NO va en la barra superior (corrección de Juan,
 * 2026-08-20: «es un lugar demasiado importante»): la esquina de arriba es del
 * trabajo del día. Vive al final del inicio, con el correo de quien está dentro,
 * como enlace discreto — se encuentra cuando se busca y no estorba cuando no.
 */
export function BotonSalir() {
  const [u, setU] = useState(null)
  useEffect(() => observarSesion(setU), [])
  if (!u) return null
  return (
    <div className="cuenta">
      <span className="s">Sesión de {u.email}</span>
      <button type="button" className="discreto" onClick={() => salir()}>Cerrar sesión</button>
    </div>
  )
}

/** Cuenta autenticada pero sin rol en los claims: el vestíbulo. */
function SinRol() {
  return (
    <div className="app sesion">
      <div className="sesion-panel">
        <LadoFoto />
        <div className="sesion-caja">
          <Identidad />
          <p className="sesion-texto">
            Tu cuenta existe pero todavía no tiene un rol asignado. Pide al administrador
            que te dé acceso y vuelve a entrar.
          </p>
          <button type="button" className="ib w" onClick={() => salir()}>Salir</button>
        </div>
      </div>
    </div>
  )
}

function Entrada() {
  const [correo, setCorreo] = useState('')
  const [clave, setClave] = useState('')
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState(false)

  const enviar = async ev => {
    ev.preventDefault()
    setError('')
    setOcupado(true)
    try {
      await entrar(correo.trim(), clave)
      // El cambio de estado lo hace observarSesion en la compuerta.
    } catch (e) {
      setError(MENSAJES[e.code] || 'No se pudo iniciar sesión. Intenta de nuevo.')
      setOcupado(false)
    }
  }

  return (
    <div className="app sesion">
      <div className="sesion-panel">
        <LadoFoto />
        <form className="sesion-caja" onSubmit={enviar}>
          <Identidad />
          <p className="sesion-texto">La plataforma interna de {MARCA.nombre}. Solo personal autorizado.</p>
          <label className="f">
            <span>Correo</span>
            <input
              type="email" value={correo} autoComplete="username" autoFocus required
              onChange={ev => setCorreo(ev.target.value)} placeholder="tu@sohamiot.com"
            />
          </label>
          <label className="f">
            <span>Contraseña</span>
            <input
              type="password" value={clave} autoComplete="current-password" required
              onChange={ev => setClave(ev.target.value)} placeholder="••••••••"
            />
          </label>
          {error && <p className="sesion-error" role="alert">{error}</p>}
          <button type="submit" className="ib pri w" disabled={ocupado || !correo || !clave}>
            {ocupado ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  )
}
